import { useEffect } from "react";
import {
  nextTrack,
  seek,
  setMuted,
  setTracks,
  setVolume,
  toggle,
  useMusic
} from "./music-engine";
function MusicPlayer({
  tracks,
  title = "The Birthday Song",
  subtitle,
  compact = false,
  className = ""
}) {
  const m = useMusic();
  const track = m.tracks[m.index] ?? tracks[0];
  useEffect(() => {
    setTracks(tracks, { autoStart: false });
  }, [tracks]);
  const fmt = (s) => {
    if (!Number.isFinite(s) || s < 0) return "0:00";
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60);
    return `${mins}:${String(secs).padStart(2, "0")}`;
  };
  if (!track) {
    return <div className={`glass rounded-2xl p-5 ${className}`}>
        <p className="eyebrow">Music</p>
        <p className="mt-2 text-sm text-[color:var(--muted-foreground)]">
          No song has been added yet.
        </p>
      </div>;
  }
  const pct = m.duration > 0 ? m.current / m.duration * 100 : 0;
  const recordShadow = m.playing ? "inset 0 0 0 1px rgba(233,168,242,.42), 0 10px 26px -10px #000" : "inset 0 0 0 1px rgba(233,168,242,.22), 0 6px 16px -12px #000";
  return <div className={`glass gold-frame rounded-2xl ${compact ? "p-4" : "p-5 sm:p-6"} ${className}`}>
      <div className="flex items-center gap-4">
        {
    /* rotating record */
  }
        <div className="relative shrink-0">
          <div
    className="grid h-12 w-12 place-items-center rounded-full sm:h-14 sm:w-14"
    style={{
      background: "conic-gradient(from 0deg, #1a1c33, #2b2550, #1a1c33, #352a63, #1a1c33)",
      boxShadow: recordShadow,
      animation: m.playing ? "spin 7s linear infinite" : void 0
    }}
  >
            <div
    className="h-3 w-3 rounded-full"
    style={{ background: "linear-gradient(180deg,#eed6ff,#9b6fd6)" }}
  />
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <p className="eyebrow">{m.playing ? "Now playing" : "Ready to play"}</p>
          <h3 className="truncate font-display text-lg text-[#f3f4f9] sm:text-xl">
            {track.caption || title}
          </h3>
          {subtitle && !track.caption && <p className="truncate text-xs text-[color:var(--muted-foreground)]">{subtitle}</p>}
        </div>

        <button
    type="button"
    onClick={() => void toggle()}
    aria-label={m.playing ? "Pause the song" : "Play the song"}
    aria-pressed={m.playing}
    className="btn-gold !min-h-[44px] !w-11 !px-0 shrink-0"
  >
          {m.playing ? <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg> : <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M8 5.14v13.72a1 1 0 0 0 1.54.84l10.3-6.86a1 1 0 0 0 0-1.68L9.54 4.3A1 1 0 0 0 8 5.14z" />
            </svg>}
        </button>
      </div>

      <div className="mt-4">
        <div className="relative">
          <div className="absolute left-0 right-0 top-[9px] h-[3px] rounded-full bg-white/15" aria-hidden="true" />
          <div
    className="absolute left-0 top-[9px] h-[3px] rounded-full"
    aria-hidden="true"
    style={{
      width: `${pct}%`,
      background: "linear-gradient(90deg,#9b6fd6,#e9a8f2)",
      boxShadow: "0 0 10px rgba(233,168,242,.6)"
    }}
  />
          <input
    type="range"
    min={0}
    max={m.duration || 0}
    step={0.1}
    value={Math.min(m.current, m.duration || 0)}
    onChange={(e) => seek(Number(e.target.value))}
    aria-label="Seek within the song"
    className="range-lux relative"
    disabled={!m.duration}
  />
        </div>
        <div className="mt-1 flex justify-between text-[0.68rem] tabular-nums text-[color:var(--muted-foreground)]">
          <span>{fmt(m.current)}</span>
          <span>{fmt(m.duration)}</span>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-3">
        <button
    type="button"
    onClick={() => setMuted(!m.muted)}
    aria-label={m.muted ? "Unmute" : "Mute"}
    aria-pressed={m.muted}
    className="grid h-9 w-9 place-items-center rounded-full border border-[rgba(233,168,242,.25)] text-[#e9a8f2] transition hover:bg-white/5"
  >
          {m.muted ? <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M4 9v6h4l5 4V5L8 9H4z" />
              <path d="M16 9l5 6M21 9l-5 6" stroke="currentColor" strokeWidth="1.6" fill="none" />
            </svg> : <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M4 9v6h4l5 4V5L8 9H4z" />
              <path d="M16.5 8.5a5 5 0 0 1 0 7" stroke="currentColor" strokeWidth="1.6" fill="none" />
            </svg>}
        </button>

        <input
    type="range"
    min={0}
    max={1}
    step={0.01}
    value={m.muted ? 0 : m.volume}
    onChange={(e) => {
      setVolume(Number(e.target.value));
      if (m.muted) setMuted(false);
    }}
    aria-label="Volume"
    className="range-lux max-w-[120px]"
  />

        {m.tracks.length > 1 && <button
    type="button"
    onClick={nextTrack}
    className="ml-auto text-xs tracking-widest text-[#a9aecb] uppercase hover:text-[#e9a8f2]"
  >
            Next track
          </button>}
      </div>

      {m.blocked && !m.playing && <p role="status" className="mt-3 text-xs text-[color:var(--gold-dim)]">
          Your browser is holding the sound until you tap — anything on the page will do.
        </p>}

      {m.error && <p role="status" className="mt-3 text-xs text-[#f3c2ce]">
          {m.error}
        </p>}
    </div>;
}
export {
  MusicPlayer
};
